import type { AgentVisibleLoopStep } from "@/types/createAgent";

interface ActivityCopy {
  title: string;
  summary: string;
}

interface WorkCopy {
  planned: ActivityCopy;
  completed: ActivityCopy;
}

// These names describe a selected next step or a confirmed successful result.
// Raw model thoughts and tool result strings are not customer-facing copy.
const WORK_COPY: Record<string, WorkCopy> = {
  classify_intent: {
    planned: {
      title: "Understand your request",
      summary: "Identifying what you want to do next.",
    },
    completed: {
      title: "Understood your request",
      summary: "Identified the next design task.",
    },
  },
  understand_user_intent: {
    planned: {
      title: "Understand your request",
      summary: "Identifying the dress or change you described.",
    },
    completed: {
      title: "Understood your request",
      summary: "Identified the dress or change you described.",
    },
  },
  inspect_brief: {
    planned: {
      title: "Review saved details",
      summary: "Checking what is already set for this dress.",
    },
    completed: {
      title: "Reviewed saved details",
      summary: "Checked what is already set for this dress.",
    },
  },
  inspect_design_brief: {
    planned: {
      title: "Review saved details",
      summary: "Checking what is already set for this dress.",
    },
    completed: {
      title: "Reviewed saved details",
      summary: "Checked what is already set for this dress.",
    },
  },
  extract_design_delta: {
    planned: {
      title: "Review your changes",
      summary: "Identifying which dress details you want to change.",
    },
    completed: {
      title: "Reviewed your changes",
      summary: "Identified the dress details you want to change.",
    },
  },
  update_design_brief: {
    planned: {
      title: "Plan detail changes",
      summary: "Working out how your preferences fit the dress.",
    },
    completed: {
      title: "Saved design details",
      summary: "Added your latest preferences to the dress design.",
    },
  },
  evaluate_readiness: {
    planned: {
      title: "Check preview readiness",
      summary: "Checking whether the design has enough detail for a preview.",
    },
    completed: {
      title: "Checked preview readiness",
      summary: "Reviewed the details needed for a preview.",
    },
  },
  decide_turn_mode: {
    planned: {
      title: "Choose the next step",
      summary: "Finding the most useful way to move your design forward.",
    },
    completed: {
      title: "Chose the next step",
      summary: "Selected the next part of the design to work on.",
    },
  },
  propose_concepts: {
    planned: {
      title: "Explore dress ideas",
      summary: "Considering design directions for your request.",
    },
    completed: {
      title: "Prepared dress ideas",
      summary: "Put together directions you can compare.",
    },
  },
  compare_options: {
    planned: {
      title: "Compare options",
      summary: "Weighing the design choices you are considering.",
    },
    completed: {
      title: "Compared options",
      summary: "Reviewed the design choices you are considering.",
    },
  },
  retrieve_catalog_options: {
    planned: {
      title: "Explore available choices",
      summary: "Looking for details that fit your dress.",
    },
    completed: {
      title: "Found design choices",
      summary: "Checked the available details for your dress.",
    },
  },
  consult_blog: {
    planned: {
      title: "Look for style guidance",
      summary: "Checking whether design guidance can help your request.",
    },
    completed: {
      title: "Consulted style guidance",
      summary: "Reviewed guidance relevant to your request.",
    },
  },
  analyze_reference_image: {
    planned: {
      title: "Review your reference photo",
      summary: "Checking which details can guide the dress.",
    },
    completed: {
      title: "Reviewed your reference photo",
      summary: "Checked the visual details in your photo.",
    },
  },
  review_generated_image: {
    planned: {
      title: "Review your preview",
      summary: "Checking the image against your design request.",
    },
    completed: {
      title: "Reviewed your preview",
      summary: "Checked the image against your design request.",
    },
  },
  critique_candidate: {
    planned: {
      title: "Check the design direction",
      summary: "Reviewing the idea before presenting it to you.",
    },
    completed: {
      title: "Checked the design direction",
      summary: "Reviewed the idea before presenting it to you.",
    },
  },
  stage_generate: {
    planned: {
      title: "Prepare a preview",
      summary: "Checking the design before offering a preview.",
    },
    completed: {
      title: "Prepared a preview option",
      summary: "Checked how to start a preview for this design.",
    },
  },
  stage_modify: {
    planned: {
      title: "Prepare an image edit",
      summary: "Working out how to apply your requested change.",
    },
    completed: {
      title: "Prepared an image edit",
      summary: "Checked how your requested change can be applied.",
    },
  },
  stage_virtual_try_on: {
    planned: {
      title: "Prepare try-on",
      summary: "Checking the dress and photo needed for try-on.",
    },
    completed: {
      title: "Prepared try-on option",
      summary: "Checked how to start try-on with this dress.",
    },
  },
  stage_quote: {
    planned: {
      title: "Prepare a quote",
      summary: "Checking the dress details needed for a quote.",
    },
    completed: {
      title: "Prepared quote option",
      summary: "Checked how to request a quote for this dress.",
    },
  },
  answer_design_question: {
    planned: {
      title: "Answer your question",
      summary: "Checking the details needed for a useful answer.",
    },
    completed: {
      title: "Worked through your question",
      summary: "Prepared an answer from the design details available.",
    },
  },
  offer_reference_upload: {
    planned: {
      title: "Consider an inspiration photo",
      summary: "Checking whether a reference photo would help.",
    },
    completed: {
      title: "Prepared photo guidance",
      summary: "Checked how an inspiration photo could help.",
    },
  },
  ask_user: {
    planned: {
      title: "Find the next question",
      summary: "Checking which detail still needs your input.",
    },
    completed: {
      title: "Prepared a question",
      summary: "Identified a detail that needs your input.",
    },
  },
  assemble_widgets: {
    planned: {
      title: "Prepare your choices",
      summary: "Organizing the options for the next step.",
    },
    completed: {
      title: "Prepared your choices",
      summary: "Organized the options for the next step.",
    },
  },
};

const THINKING: ActivityCopy = {
  title: "Thinking through your request",
  summary: "Reading your message and the current design.",
};

const REPLY: ActivityCopy = {
  title: "Preparing your reply",
  summary: "Putting the design work and next step into a clear reply.",
};

/** Project structured activity into copy without exposing raw model/tool text. */
export function presentThinkingStep(
  step: Pick<
    AgentVisibleLoopStep,
    "source" | "phase" | "outcome" | "toolName" | "kind"
  >
): ActivityCopy {
  if (step.source === "llm" && step.phase === "error") {
    return {
      title: "Checking the next step",
      summary: "Reviewing the design details available before responding.",
    };
  }

  if (step.source === "llm" && step.phase === "request") return THINKING;

  if (step.source === "tool" && step.outcome === "skipped") {
    return {
      title: "Checking another approach",
      summary: "That design step did not complete.",
    };
  }

  if (step.source === "tool" && step.outcome !== "ok") {
    return {
      title: "Checking the result",
      summary: "Reviewing how that design step turned out.",
    };
  }

  if (step.toolName === "plan_response" || step.toolName === "final_response") {
    return REPLY;
  }

  const work = step.toolName ? WORK_COPY[step.toolName] : undefined;
  if (work) return step.source === "tool" ? work.completed : work.planned;

  return THINKING;
}

/** The live single-line status (including the mobile indicator) uses the same copy. */
export function presentThinkingStatus(
  step?: Pick<
    AgentVisibleLoopStep,
    "source" | "phase" | "outcome" | "toolName" | "kind"
  >
): string {
  return presentThinkingStep(step ?? { kind: "thought" }).summary;
}
